import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import type {
	ContentProps,
	DialogContentProps,
	PopupProps,
	TriggerProps,
} from '@atlaskit/popup/types';
import { applyInitialFocus } from '@atlaskit/top-layer/apply-initial-focus';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types/types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container/ui';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ErrorContent } from '../error-content';
import { useContentReadySignal } from '../use-content-ready-signal';
import { useStatusAnnouncement } from '../use-status-announcement';
import { CloseOnItemClick } from './close-on-item-click';
import { defaultErrorText } from './default-error-text';
import { FocusErrorExperience } from './focus-error-experience';
import { FocusInsideOnLoad } from './focus-inside-on-load';
import { FocusOnContainerOnLoad } from './focus-on-container-on-load';
import { isFocusMovingRole } from './is-focus-moving-role';
import { Loading } from './loading';
import { useClickToCloseRef } from './use-click-to-close-ref';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	loadingText,
	onMount,
	onUnmount,
	onFocusStateChange,
	shouldFocus,
	role,
}: {
	fallback?: ReactNode;
	loadingText?: ReactNode;
	onMount: () => void;
	onUnmount: () => void;
	onFocusStateChange: (wasFocused: boolean) => void;
	shouldFocus: boolean;
	role?: PopupProps['role'];
}) => {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (shouldFocus) {
			if (fg('platform_dst-automated_focus')) {
				const container = ref.current?.parentElement;
				if (container && isFocusMovingRole({ role })) {
					applyInitialFocus({ container, initialFocusRef: ref, role });
				}
			} else {
				ref.current?.focus();
			}
		}

		onMount();
		return () => onUnmount();
	}, [onMount, onUnmount, shouldFocus]);

	return (
		<span
			ref={ref}
			tabIndex={shouldFocus ? -1 : undefined}
			data-testid="popup-trigger-loading"
			onBlur={() => onFocusStateChange(false)}
			onFocus={() => onFocusStateChange(true)}
		>
			{fallback || <Loading loadingText={loadingText} />}
		</span>
	);
};

/**
 * Whether the popup's own initial focus target should be forwarded to the
 * entry point and used to restore focus.
 *
 * Both gates are always read, rather than short-circuited, so that each gate
 * is accessed regardless of the other gate's value.
 */
function isInitialFocusForwardingEnabled(): boolean {
	const isAutomatedFocusEnabled = fg('platform_dst-automated_focus');
	const isInitialFocusRefEnabled = fg('platform_dst-popup-trigger-initial-focus-ref');
	return isAutomatedFocusEnabled || isInitialFocusRefEnabled;
}

export type PopupTriggerProps<TEntryPoint> = {
	/**
	 * Entry point to load when the popup opens.
	 */
	entryPoint: TEntryPoint;
	/**
	 * Parameters passed when loading the entry point.
	 */
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	/**
	 * Runtime props passed to the loaded entry point.
	 */
	entryPointProps?: Omit<EntryPointProps<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	/**
	 * Renders content when the entry point fails to load or render. When omitted, the built-in
	 * accessible error UI is used; use `errorTitle` and `errorText` to provide localized error
	 * content before replacing it.
	 */
	errorFallback?: ErrorFallback;
	/**
	 * The title that renders during the error screen.
	 */
	errorTitle?: ReactNode;
	/**
	 * The text rendered during the error screen and announced in the live region.
	 */
	errorText?: ReactNode;
	/**
	 * Renders while the entry point is loading. When omitted, the built-in accessible loading UI is
	 * used; use `loadingText` and `loadingCompleteText` to provide localized status text before
	 * replacing it.
	 */
	fallback?: ReactNode;
	/**
	 * Reports errors to UFO even when the entry point does not throw.
	 */
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	forcedReportErrorUFO?: boolean;
	/**
	 * Called when the entry point fails to load or render.
	 */
	onError?: ErrorCallback;
	/**
	 * Called synchronously once when activating the trigger starts opening a closed popup,
	 * immediately after the open-state update is queued. The popup may not have rendered yet,
	 * and its entry-point content may still be loading; this does not confirm content is visible.
	 * Preloading, closing, and content finishing loading do not call it.
	 */
	onPopupOpen?: () => void;
	/**
	 * Preloads the entry point when the trigger receives focus.
	 */
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	/**
	 * The text that renders in the default loading fallback and is announced to users of assistive
	 * technology when loading starts, including when a custom `fallback` is provided.
	 */
	loadingText?: ReactNode;
	/**
	 * The text that is read to users of assistive technology when loading is
	 * complete. This is only read if the loading fallback is mounted.
	 */
	loadingCompleteText?: ReactNode;
	/**
	 * Closes the popup after the user activates a link, button or menu item in the loaded content,
	 * so the content does not have to call `onClose` from every item. The item's own click handler
	 * runs first.
	 *
	 * Prefer `DropdownMenu` when building a menu, as it provides the menu semantics and behavior,
	 * including closing after an eligible item is activated. If you build a menu with a popup, set
	 * its `role` to `"menu"` so assistive technologies receive the menu context. A popup's menu
	 * role alone does not close it when an item is activated; use this prop when eligible item
	 * activations should close the popup.
	 *
	 * The popup stays open for modifier clicks (which open a new tab or window), and for items that
	 * are disabled (`aria-disabled="true"`), expand or open something (`aria-expanded`,
	 * `aria-haspopup`), or toggle a selection (`menuitemcheckbox`, `menuitemradio`). An item can
	 * also keep it open by stopping the click's propagation.
	 */
	shouldCloseOnItemClick?: boolean;
	/**
	 * Renders the control that opens the popup.
	 */
	trigger: (triggerProps: TriggerProps) => ReactNode;
} & CustomPopupProps;

export function PopupTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	errorText,
	errorTitle,
	onError,
	forcedReportErrorUFO,
	onClose,
	onPopupOpen,
	preloadOnFocus = true,
	autoFocus,
	loadingText = 'Loading popup...',
	loadingCompleteText = 'Loading is complete.',
	shouldCloseOnItemClick = false,
	...popupProps
}: PopupTriggerProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);
	const { activateStatusAnnouncement, announce, cancelPendingAnnouncement, resetStatus } =
		useStatusAnnouncement();
	const isOpenRef = useRef(false);
	const hasErrorRef = useRef(false);
	const triggerFocusRef = useRef<HTMLElement>(null);

	const updateRef = useRef<(() => void) | null>(null);
	const setInitialFocusRef = useRef<DialogContentProps['setInitialFocusRef'] | null>(null);
	const initialFocusRef = useRef<HTMLElement | null>(null);
	const shouldTransferFocusRef = useRef(false);

	const handleError = useCallback<ErrorCallback>(
		(props) => {
			if (isOpenRef.current) {
				hasErrorRef.current = true;
				resetStatus();
				announce({ text: errorText ?? defaultErrorText, delay: 100, clearAfter: 5000 });
			}
			onError?.(props);
		},
		[announce, errorText, onError, resetStatus],
	);

	const handleOpen = useCallback(() => {
		// Popups can load without moving focus into a focus-trapped container, so
		// we need to ensure that changes are announced to users.
		activateStatusAnnouncement();
		isOpenRef.current = true;
		setIsOpen(true);
		// Notify at activation time, before popup content is queued for rendering. The native
		// opening listener can commit state before the trigger's React onClick runs.
		onPopupOpen?.();
	}, [activateStatusAnnouncement, onPopupOpen]);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			isOpenRef.current = false;
			setIsOpen(false);
			if (
				isInitialFocusForwardingEnabled() &&
				popupProps.shouldReturnFocus !== false &&
				event?.type === 'keydown' &&
				'key' in event &&
				(event.key === 'Escape' || event.key === 'Esc')
			) {
				setTimeout(() => {
					if (document.activeElement === document.body) {
						triggerFocusRef.current?.focus();
					}
				}, 0);
			}
			resetStatus();
			onClose?.(event, currentLevel);
		},
		[onClose, popupProps.shouldReturnFocus, resetStatus, triggerFocusRef],
	);

	// Content that closes the popup from its own item handler has already closed it by the time
	// this runs, so it must not report a second close.
	const handleItemClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			if (isOpenRef.current) {
				handleClose(event);
			}
		},
		[handleClose],
	);

	const triggerCloseRef = useClickToCloseRef({
		isOpen,
		handleClose,
	});
	const { onContentReady, resetContentReady, waitForContentReady } = useContentReadySignal();

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const popupSetInitialFocusRef = useRef<((el: HTMLElement | null) => void) | null>(null);
	const handleSetInitialFocusRef = useCallback((el: HTMLElement | null) => {
		initialFocusRef.current = el;
		popupSetInitialFocusRef.current?.(el);
	}, []);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// Use the rendered open state: the separate close listener may already have
			// reset isOpenRef on this click. Reading that ref would reopen the popup.
			if (isOpen) {
				return;
			}

			resetContentReady();
			hasErrorRef.current = false;
			shouldTransferFocusRef.current = false;
			handleOpen();

			const entryPointContainer = (
				<InternalEntryPointContainer
					id={id}
					onContentReady={onContentReady}
					entryPointReference={entryPointReference}
					fallback={
						// when the fallback is unmounted we want to trigger an update to the popup position
						<FallbackComponent
							fallback={fallback}
							loadingText={loadingText}
							onMount={() => {
								announce({ text: loadingText, delay: 100 });
							}}
							onUnmount={() => {
								if (!hasErrorRef.current) {
									const wasPending = cancelPendingAnnouncement();
									if (!wasPending && isOpenRef.current) {
										announce({ text: loadingCompleteText, clearAfter: 5000 });
									}
								}

								return updateRef.current?.();
							}}
							onFocusStateChange={(wasFocused) => {
								shouldTransferFocusRef.current = wasFocused;
							}}
							shouldFocus={autoFocus !== false}
							role={popupProps.role}
						/>
					}
					errorFallback={(props) => (
						<FocusErrorExperience
							role={popupProps.role}
							shouldFocus={autoFocus !== false}
							shouldTransferFocusRef={shouldTransferFocusRef}
							triggerRef={triggerFocusRef}
						>
							{errorFallback ? (
								errorFallback(props)
							) : (
								<ErrorContent title={errorTitle} errorText={errorText ?? defaultErrorText} />
							)}
						</FocusErrorExperience>
					)}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							onContentResized: () => {
								updateRef.current?.();
							},
							onClose: handleClose,
							...(isInitialFocusForwardingEnabled()
								? { setInitialFocusRef: handleSetInitialFocusRef }
								: {}),
						} as unknown as EntryPointProps<TEntryPoint>
					}
					onError={handleError}
					forcedError={forcedReportErrorUFO}
				/>
			);

			setContent(
				<>
					{shouldCloseOnItemClick ? (
						<CloseOnItemClick onItemClick={handleItemClick}>{entryPointContainer}</CloseOnItemClick>
					) : (
						entryPointContainer
					)}
					{/* Omit means `Popup`'s own default (`true`). Only an explicit
					`false` opts out of post-load focus. */}
					{autoFocus !== false &&
						(fg('platform_dst-popup-trigger-focus-container') &&
						!fg('platform_dst-automated_focus') ? (
							<FocusOnContainerOnLoad
								entryPointReference={entryPointReference}
								setInitialFocusRef={setInitialFocusRef}
								waitForContentReady={waitForContentReady}
							/>
						) : (
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								initialFocusRef={initialFocusRef}
								role={popupProps.role}
								setInitialFocusRef={setInitialFocusRef}
								shouldTransferFocusRef={shouldTransferFocusRef}
								triggerRef={triggerFocusRef}
								waitForContentReady={waitForContentReady}
							/>
						))}
				</>,
			);
		},
		[
			setContent,
			id,
			isOpen,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			errorText,
			errorTitle,
			handleOpen,
			handleError,
			announce,
			cancelPendingAnnouncement,
			handleClose,
			handleSetInitialFocusRef,
			autoFocus,
			loadingCompleteText,
			loadingText,
			onContentReady,
			resetContentReady,
			shouldCloseOnItemClick,
			waitForContentReady,
			handleItemClick,
			popupProps.role,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef, triggerFocusRef]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, triggerRef, triggerCloseRef, triggerFocusRef],
	);

	const popupContent = useCallback(
		({ update, setInitialFocusRef }: DialogContentProps) => {
			updateRef.current = update;
			if (isInitialFocusForwardingEnabled()) {
				popupSetInitialFocusRef.current = setInitialFocusRef;
			}
			return content;
		},
		[content],
	);

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent -- This would be complex to rollout and top layer is rolling this out soon */}
			<Popup
				// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
				{...popupProps}
				autoFocus={autoFocus}
				UNSAFE__disableInitialAutoFocus={fg('platform_dst-automated_focus')}
				isOpen={isOpen}
				onClose={handleClose}
				trigger={popupTrigger}
				// Run time passes the setter for every role; the types hide it. Goes at the flag cleanup.
				content={popupContent as PopupProps['content']}
			/>
		</>
	);
}
